import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownRight, ChevronRight, X, Radio, Activity } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLiveQuotes } from "@/hooks/useLiveQuotes";
import { usePollingQuotes } from "@/hooks/usePollingQuotes";
import NextScanCountdown from "@/components/NextScanCountdown";
import ScanFunnel from "@/components/ScanFunnel";

function num(v, digits = 2) {
  const n = parseFloat(v ?? 0);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export default function Dashboard() {
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [watch, setWatch] = useState([]);
  const [perf, setPerf] = useState(null);
  const [closedTrades, setClosedTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(null);
  const [confirmSymbol, setConfirmSymbol] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [a, p, w, perfRes, closedRes] = await Promise.all([
        api.get("/account"),
        api.get("/positions"),
        api.get("/market/watchlist"),
        api.get("/portfolio/performance").catch(() => ({ data: null })),
        api.get("/portfolio/closed_trades?limit=20").catch(() => ({ data: [] })),
      ]);
      setAccount(a.data);
      setPositions(p.data || []);
      setWatch(w.data || []);
      setPerf(perfRes.data);
      setClosedTrades(closedRes.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  // Live tick prices for watchlist + open positions
  const liveSymbols = useMemo(() => {
    const set = new Set();
    (watch || []).forEach((q) => q?.symbol && set.add(q.symbol));
    (positions || []).forEach((p) => p?.symbol && set.add(p.symbol));
    return Array.from(set);
  }, [watch, positions]);
  const { quotes: live, connected: liveConnected, tickCount, lastTickAt, lastError, demoMode } = usePollingQuotes(liveSymbols);
  const [, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const tickAgo = lastTickAt ? Math.round((Date.now() - lastTickAt) / 1000) : null;

  const [diagRunning, setDiagRunning] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const runDiagnostic = async () => {
    setDiagRunning(true);
    setDiagResult(null);
    try {
      const r = await api.post("/diagnostics/stream");
      setDiagResult(r.data);
      if (r.data.ok) {
        toast.success(`Stream OK · ${r.data.ticks} ticks in ${r.data.duration_sec}s`);
      } else {
        toast.error(r.data.hint || "Stream test failed", { duration: 12000 });
      }
    } catch (err) {
      toast.error("Diagnostic failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setDiagRunning(false);
    }
  };

  const closePosition = async (symbol) => {
    setClosing(symbol);
    setConfirmSymbol(null);
    try {
      await api.post(`/positions/${symbol}/close`);
      toast.success(`Close order placed for ${symbol}`);
      setTimeout(load, 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not close ${symbol}`);
    } finally {
      setClosing(null);
    }
  };

  const equity = parseFloat(account?.equity || 0);
  const lastEquity = parseFloat(account?.last_equity || equity);
  const dayPL = equity - lastEquity;
  const dayPLPct = lastEquity ? (dayPL / lastEquity) * 100 : 0;
  const positive = dayPL >= 0;

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="mono text-[10px] tracking-[0.22em] text-[#71717A]">PORTFOLIO EQUITY</div>
          <div className="flex items-baseline gap-4 mt-2">
            <div className="text-5xl font-black mono tracking-tight" data-testid="equity-value">
              ${num(equity)}
            </div>
            <div className={`mono text-sm ${positive ? "gain" : "loss"} flex items-center gap-1`} data-testid="day-pl">
              {positive ? <ArrowUpRight size={14}/> : <ArrowDownRight size={14}/>}
              ${num(Math.abs(dayPL))} ({num(dayPLPct)}%)
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate("/trade")}
          data-testid="quick-trade-btn"
          className="bg-white text-black mono text-xs tracking-[0.22em] font-bold px-5 py-2.5 hover:bg-[#D4D4D8]"
        >
          NEW ORDER →
        </button>
      </div>

      <NextScanCountdown />

      <ScanFunnel />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#27272A] border border-[#27272A]" data-testid="stats-row">
        {[
          { label: "BUYING POWER", value: account?.buying_power },
          { label: "CASH", value: account?.cash },
          { label: "PORTFOLIO VALUE", value: account?.portfolio_value },
          { label: "STATUS", value: account?.status, isText: true },
        ].map((s) => (
          <div key={s.label} className="bg-[#141415] p-4">
            <div className="mono text-[10px] tracking-[0.22em] text-[#71717A]">{s.label}</div>
            <div className="mono text-xl mt-2">
              {s.isText ? (s.value || "—") : `$${num(s.value)}`}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Positions */}
        <div className="lg:col-span-2 panel" data-testid="positions-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#27272A]">
            <div className="mono text-[11px] tracking-[0.22em]">OPEN POSITIONS · {positions.length}</div>
            <button onClick={load} data-testid="refresh-positions" className="mono text-[10px] text-[#71717A] hover:text-white">REFRESH</button>
          </div>
          {positions.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <div className="mono text-xs text-[#71717A] tracking-widest">NO POSITIONS</div>
              <div className="text-[#A1A1AA] text-sm mt-2">Place your first order or enable the MACD bot.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[#71717A] mono text-[10px] tracking-widest border-b border-[#27272A]">
                    <th className="text-left px-4 py-2 font-normal">SYMBOL</th>
                    <th className="text-right px-4 py-2 font-normal">QTY</th>
                    <th className="text-right px-4 py-2 font-normal">AVG</th>
                    <th className="text-right px-4 py-2 font-normal">PRICE</th>
                    <th className="text-right px-4 py-2 font-normal">MKT VAL</th>
                    <th className="text-right px-4 py-2 font-normal">P&L</th>
                    <th className="text-right px-4 py-2 font-normal w-28">ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((p) => {
                    const livePrice = live[p.symbol]?.price;
                    const currentPrice = livePrice ?? parseFloat(p.current_price);
                    const qty = parseFloat(p.qty);
                    const avg = parseFloat(p.avg_entry_price);
                    const mktValue = livePrice != null ? currentPrice * qty : parseFloat(p.market_value);
                    const pl = livePrice != null ? (currentPrice - avg) * qty : parseFloat(p.unrealized_pl);
                    const plPct = avg ? ((currentPrice - avg) / avg) : parseFloat(p.unrealized_plpc);
                    const positive = pl >= 0;
                    const isConfirming = confirmSymbol === p.symbol;
                    const isClosing = closing === p.symbol;
                    const isPulsing = livePrice != null && Date.now() - (live[p.symbol]?.lastUpdate || 0) < 1500;
                    return (
                      <tr key={p.symbol} className="border-b border-[#27272A] hover:bg-[#1C1C1E]" data-testid={`position-row-${p.symbol}`}>
                        <td className="px-4 py-2.5 font-bold mono">{p.symbol}</td>
                        <td className="px-4 py-2.5 text-right mono">{num(qty, 0)}</td>
                        <td className="px-4 py-2.5 text-right mono text-[#A1A1AA]">{num(avg)}</td>
                        <td className={`px-4 py-2.5 text-right mono transition-colors ${isPulsing ? (positive ? "text-[#00E676]" : "text-[#FF3B30]") : ""}`}>
                          {num(currentPrice)}
                        </td>
                        <td className="px-4 py-2.5 text-right mono">${num(mktValue)}</td>
                        <td className={`px-4 py-2.5 text-right mono ${positive ? "gain" : "loss"}`}>
                          {positive ? "+" : ""}${num(pl)} ({num(plPct * 100)}%)
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {isConfirming ? (
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => closePosition(p.symbol)}
                                disabled={isClosing}
                                data-testid={`dash-confirm-close-${p.symbol}`}
                                className="bg-[#FF3B30] text-white px-2 py-1 mono text-[10px] tracking-widest font-bold hover:bg-[#E5342B] disabled:opacity-50"
                              >
                                {isClosing ? "..." : "CONFIRM"}
                              </button>
                              <button
                                onClick={() => setConfirmSymbol(null)}
                                disabled={isClosing}
                                data-testid={`dash-cancel-close-${p.symbol}`}
                                className="border border-[#27272A] text-[#A1A1AA] px-2 py-1 mono text-[10px] tracking-widest hover:border-[#52525B]"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmSymbol(p.symbol)}
                              data-testid={`dash-close-pos-${p.symbol}`}
                              className="inline-flex items-center gap-1 border border-[#FF3B30]/40 text-[#FF3B30] px-2 py-1 mono text-[10px] tracking-widest font-bold hover:bg-[#FF3B30] hover:text-white transition-colors"
                            >
                              <X size={10} /> CLOSE
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Watchlist */}
        <div className="panel" data-testid="watchlist-panel">
          <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between flex-wrap gap-2">
            <div className="mono text-[11px] tracking-[0.22em]">WATCHLIST · TOP TECH/AI</div>
            <div className="flex items-center gap-3 mono text-[10px]" data-testid="live-status">
              <div className="flex items-center gap-1.5">
                <Radio size={11} className={liveConnected ? "text-[#00E676] animate-pulse" : "text-[#71717A]"} />
                <span className={liveConnected ? "text-[#00E676]" : "text-[#71717A]"}>
                  {liveConnected ? "LIVE" : "OFFLINE"}
                </span>
              </div>
              <span className="text-[#71717A]" data-testid="tick-count">UPDATES {tickCount}</span>
              <span className="text-[#71717A]" data-testid="last-tick">
                {tickAgo == null ? "—" : tickAgo < 60 ? `${tickAgo}s ago` : `${Math.round(tickAgo/60)}m ago`}
              </span>
              {lastError && (
                <span className="loss truncate max-w-[260px]" title={lastError} data-testid="ws-error">
                  ERR: {lastError.slice(0, 40)}
                </span>
              )}
              <button
                onClick={runDiagnostic}
                disabled={diagRunning}
                data-testid="run-stream-diagnostic-btn"
                className="inline-flex items-center gap-1 border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#52525B] px-2 py-0.5 mono text-[10px] tracking-widest disabled:opacity-50"
                title="Run an 8-second Alpaca stream diagnostic"
              >
                <Activity size={10} /> {diagRunning ? "TESTING..." : "TEST"}
              </button>
            </div>
          </div>
          {diagResult && (
            <div className="px-4 py-2 border-b border-[#27272A] mono text-[10px]" data-testid="diag-result">
              <div className="flex items-start gap-3 flex-wrap">
                <span className={diagResult.ok ? "gain" : "loss"}>
                  {diagResult.ok ? "✓ OK" : "✗ FAIL"}
                </span>
                <span className="text-[#A1A1AA]">stage={diagResult.stage}</span>
                <span className="text-[#A1A1AA]">trades={diagResult.ticks}</span>
                <span className="text-[#A1A1AA]">quotes={diagResult.quotes ?? 0}</span>
                <span className="text-[#A1A1AA]">duration={diagResult.duration_sec}s</span>
                {diagResult.market_open !== undefined && diagResult.market_open !== null && (
                  <span className={diagResult.market_open ? "gain" : "loss"}>
                    market_{diagResult.market_open ? "OPEN" : "CLOSED"}
                  </span>
                )}
                {diagResult.endpoint && (
                  <span className="text-[#71717A]" title={diagResult.endpoint}>
                    feed={diagResult.endpoint.split("/").pop()}
                  </span>
                )}
                {diagResult.error && <span className="loss">err: {diagResult.error}</span>}
              </div>
              {diagResult.hint && <div className="text-[#A1A1AA] mt-1">{diagResult.hint}</div>}
              {diagResult.alpaca_logs && (
                <details className="mt-1">
                  <summary className="text-[#71717A] cursor-pointer text-[10px]">show alpaca SDK logs</summary>
                  <pre className="mt-1 text-[#71717A] text-[9px] whitespace-pre-wrap bg-[#0A0A0A] p-2 border border-[#27272A] max-h-48 overflow-y-auto">{diagResult.alpaca_logs}</pre>
                </details>
              )}
            </div>
          )}
          <div className="max-h-[480px] overflow-y-auto divide-y divide-[#27272A]">
            {loading && <div className="px-4 py-6 text-[#71717A] mono text-xs">LOADING...</div>}
            {watch.map((q) => {
              const livePrice = live[q.symbol]?.price;
              const price = livePrice ?? q.price;
              const change = livePrice != null ? livePrice - q.prev_close : q.change;
              const changePct = q.prev_close ? (change / q.prev_close) * 100 : q.change_pct;
              const positive = change >= 0;
              const isPulsing = livePrice != null && Date.now() - (live[q.symbol]?.lastUpdate || 0) < 1500;
              return (
                <div
                  key={q.symbol}
                  onClick={() => navigate(`/trade?symbol=${q.symbol}`)}
                  data-testid={`watch-row-${q.symbol}`}
                  className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-[#1C1C1E]"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-bold mono text-sm">{q.symbol}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`mono text-sm transition-colors ${isPulsing ? (positive ? "text-[#00E676]" : "text-[#FF3B30]") : ""}`}>
                      {num(price)}
                    </span>
                    <span className={`mono text-xs ${positive ? "gain" : "loss"} w-16 text-right`}>
                      {positive ? "+" : ""}{num(changePct)}%
                    </span>
                    <ChevronRight size={13} className="text-[#71717A]" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Performance card */}
      <div className="panel" data-testid="performance-panel">
        <div className="px-4 py-3 border-b border-[#27272A] mono text-[11px] tracking-[0.22em]">
          PERFORMANCE
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#27272A] border-b border-[#27272A]">
          {[
            { key: "today",    label: "TODAY" },
            { key: "mtd",      label: "MONTH-TO-DATE" },
            { key: "ytd",      label: "YEAR-TO-DATE" },
            { key: "all_time", label: "ALL-TIME" },
          ].map(({ key, label }) => {
            const v = perf?.[key] || { pnl: 0, pnl_pct: 0 };
            const positive = (v.pnl ?? 0) >= 0;
            return (
              <div key={key} className="px-4 py-4" data-testid={`perf-${key}`}>
                <div className="mono text-[10px] tracking-[0.22em] text-[#71717A]">{label}</div>
                <div className={`mt-1 text-2xl font-black mono ${positive ? "gain" : "loss"}`}>
                  {positive ? "+" : ""}${num(v.pnl)}
                </div>
                <div className={`mono text-xs ${positive ? "gain" : "loss"}`}>
                  {positive ? "+" : ""}{num(v.pnl_pct)}%
                </div>
              </div>
            );
          })}
        </div>
        <div className="px-4 py-2 mono text-[10px] text-[#71717A]">
          Realized + unrealized · sourced from Alpaca portfolio history
        </div>
      </div>

      {/* Closed trades table */}
      <div className="panel" data-testid="closed-trades-panel">
        <div className="px-4 py-3 border-b border-[#27272A] mono text-[11px] tracking-[0.22em] flex items-center justify-between">
          <span>CLOSED TRADES · {closedTrades.length}</span>
          <button onClick={() => navigate("/portfolio")} className="mono text-[10px] text-[#71717A] hover:text-white">
            FULL HISTORY →
          </button>
        </div>
        {closedTrades.length === 0 ? (
          <div className="p-8 text-center text-[#71717A] mono text-xs">NO CLOSED TRADES YET</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[#71717A] mono text-[10px] tracking-widest border-b border-[#27272A]">
                  <th className="text-left px-4 py-2 font-normal">CLOSED</th>
                  <th className="text-left px-4 py-2 font-normal">SYMBOL</th>
                  <th className="text-left px-4 py-2 font-normal">SIDE</th>
                  <th className="text-right px-4 py-2 font-normal">QTY</th>
                  <th className="text-right px-4 py-2 font-normal">ENTRY</th>
                  <th className="text-right px-4 py-2 font-normal">EXIT</th>
                  <th className="text-right px-4 py-2 font-normal">P&L</th>
                  <th className="text-right px-4 py-2 font-normal">RETURN</th>
                </tr>
              </thead>
              <tbody>
                {closedTrades.map((t, i) => {
                  const positive = t.pnl >= 0;
                  return (
                    <tr key={`${t.symbol}-${t.exit_at}-${i}`} className="border-b border-[#27272A]" data-testid={`closed-trade-${t.symbol}-${i}`}>
                      <td className="px-4 py-2 mono text-xs text-[#A1A1AA]">
                        {t.exit_at ? new Date(t.exit_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-2 mono font-bold">{t.symbol}</td>
                      <td className={`px-4 py-2 mono ${t.side === "LONG" ? "gain" : "loss"}`}>{t.side}</td>
                      <td className="px-4 py-2 mono text-right">{num(t.qty, 0)}</td>
                      <td className="px-4 py-2 mono text-right text-[#A1A1AA]">${num(t.entry_price)}</td>
                      <td className="px-4 py-2 mono text-right">${num(t.exit_price)}</td>
                      <td className={`px-4 py-2 mono text-right ${positive ? "gain" : "loss"}`}>
                        {positive ? "+" : ""}${num(t.pnl)}
                      </td>
                      <td className={`px-4 py-2 mono text-right ${positive ? "gain" : "loss"}`}>
                        {positive ? "+" : ""}{num(t.pnl_pct)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
